import { Dropdown, IDropdownOption, IDropdownProps, IDropdownStyles, Icon, Stack, Text, TooltipHost } from "@fluentui/react";
import React from "react";
import { CatalystTabTooltips } from "../../Common/HealthView.constants";

export interface CatalystFilterValues {
  customer: string;
  tpAccountName: string;
  action: string;
  segment: string;
  industry: string;
}

export interface CatalystFilterBarProps {
  filterValues: CatalystFilterValues;
  customerOptions: IDropdownOption[];
  tpAccountNameOptions: IDropdownOption[];
  actionOptions: IDropdownOption[];
  segmentOptions: IDropdownOption[];
  industryOptions: IDropdownOption[];
  onFilterChange: (key: keyof CatalystFilterValues, value: string) => void;
  metricFilterLabel?: string;
  metricFilterValue?: string;
  metricFilterOptions?: IDropdownOption[];
  onMetricFilterChange?: (value: string) => void;
}

const ALL_OPTION = "ALL";

const pillDropdownStyles: Partial<IDropdownStyles> = {
  root: { minWidth: 0, width: "auto" },
  dropdown: {
    border: "1px solid #0078D4",
    borderRadius: 16,
    backgroundColor: "#EFF6FC",
    minWidth: 200,
    selectors: {
      ":hover": { borderColor: "#106EBE" },
      ":focus::after": { border: "none" },
    },
  },
  title: {
    fontSize: 14,
    color: "#0078D4",
    fontWeight: 600,
    lineHeight: 28,
    height: 28,
    borderRadius: 16,
    backgroundColor: "transparent",
    border: "none",
    paddingLeft: 10,
    paddingRight: 24,
  },
  caretDownWrapper: { top: 4, right: 6, lineHeight: 28, height: 28 },
  caretDown: { fontSize: 10, color: "#0078D4" },
  dropdownItem: { fontSize: 14, minHeight: 32 },
  dropdownItemSelected: { fontSize: 14, minHeight: 32, backgroundColor: "#EFF6FC" },
};

const metricDropdownStyles: Partial<IDropdownStyles> = {
  ...pillDropdownStyles,
  dropdown: {
    border: "1px solid #0078D4",
    borderRadius: 16,
    backgroundColor: "#EFF6FC",
    minWidth: 340,
    selectors: {
      ":hover": { borderColor: "#106EBE" },
      ":focus::after": { border: "none" },
    },
  },
  title: {
    fontSize: 14,
    color: "#0078D4",
    fontWeight: 600,
    lineHeight: 28,
    height: 28,
    borderRadius: 16,
    backgroundColor: "transparent",
    border: "none",
    paddingLeft: 10,
    paddingRight: 24,
    minWidth: 340,
  },
};

const commonDropdownProps: Partial<IDropdownProps> = {
  calloutProps: {
    directionalHint: 4, // DirectionalHint.bottomLeftEdge
    calloutMaxHeight: 300,
  },
};

const buildOptions = (values: string[]): IDropdownOption[] => {
  const options: IDropdownOption[] = [{ key: ALL_OPTION, text: ALL_OPTION }];
  values.forEach((v) => {
    if (v) options.push({ key: v, text: v });
  });
  return options;
};

export const CatalystFilterBar: React.FC<CatalystFilterBarProps> = ({
  filterValues,
  customerOptions,
  tpAccountNameOptions,
  actionOptions,
  segmentOptions,
  industryOptions,
  onFilterChange,
  metricFilterLabel,
  metricFilterValue,
  metricFilterOptions,
  onMetricFilterChange,
}) => {
  return (
    <Stack tokens={{ childrenGap: 10 }} styles={{ root: { padding: "12px 0" } }}>
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
        <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
          Filters / Slicers
        </Text>
        <TooltipHost content={CatalystTabTooltips.FiltersHeader}>
          <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
        </TooltipHost>
      </Stack>
      {/* Filter Pills Row */}
      <Stack horizontal wrap tokens={{ childrenGap: 8 }} verticalAlign="center">
        <Dropdown
          placeholder="Customer"
          selectedKey={filterValues.customer}
          options={customerOptions}
          onChange={(_, opt) => onFilterChange("customer", (opt?.key as string) ?? ALL_OPTION)}
          onRenderTitle={(options) => (
            <span>Customer: <strong>{options?.[0]?.text ?? ALL_OPTION}</strong></span>
          )}
          styles={pillDropdownStyles}
          {...commonDropdownProps}
        />
        <Dropdown
          placeholder="TP Account Name"
          selectedKey={filterValues.tpAccountName}
          options={tpAccountNameOptions}
          onChange={(_, opt) => onFilterChange("tpAccountName", (opt?.key as string) ?? ALL_OPTION)}
          onRenderTitle={(options) => (
            <span>TP Account Name: <strong>{options?.[0]?.text ?? ALL_OPTION}</strong></span>
          )}
          styles={pillDropdownStyles}
          {...commonDropdownProps}
        />
        <Dropdown
          placeholder="Action Item"
          selectedKey={filterValues.action}
          options={actionOptions}
          onChange={(_, opt) => onFilterChange("action", (opt?.key as string) ?? ALL_OPTION)}
          onRenderTitle={(options) => (
            <span>Action Item: <strong>{options?.[0]?.text ?? ALL_OPTION}</strong></span>
          )}
          styles={pillDropdownStyles}
          {...commonDropdownProps}
        />
        <Dropdown
          placeholder="Segment"
          selectedKey={filterValues.segment}
          options={segmentOptions}
          onChange={(_, opt) => onFilterChange("segment", (opt?.key as string) ?? ALL_OPTION)}
          onRenderTitle={(options) => (
            <span>Segment: <strong>{options?.[0]?.text ?? ALL_OPTION}</strong></span>
          )}
          styles={pillDropdownStyles}
          {...commonDropdownProps}
        />
        <Dropdown
          placeholder="Industry"
          selectedKey={filterValues.industry}
          options={industryOptions}
          onChange={(_, opt) => onFilterChange("industry", (opt?.key as string) ?? ALL_OPTION)}
          onRenderTitle={(options) => (
            <span>Industry: <strong>{options?.[0]?.text ?? ALL_OPTION}</strong></span>
          )}
          styles={pillDropdownStyles}
          {...commonDropdownProps}
        />
        {!!metricFilterOptions?.length && (
          <Dropdown
            placeholder="Metric"
            selectedKey={metricFilterValue ?? ALL_OPTION}
            options={metricFilterOptions}
            onChange={(_, opt) => onMetricFilterChange?.((opt?.key as string) ?? ALL_OPTION)}
            onRenderTitle={(options) => (
              <span>{(metricFilterLabel ?? "Metric")}: <strong>{options?.[0]?.text ?? ALL_OPTION}</strong></span>
            )}
            styles={metricDropdownStyles}
            {...commonDropdownProps}
          />
        )}
      </Stack>
    </Stack>
  );
};

export { buildOptions };
